import {
  deleteProject,
  getProjectDetails,
  getProjectDocumentById,
  getProjectInsights,
  getProjectsList,
} from '@/app/services/projectsService';
import { APIProject, Insight } from '@/app/types/models';
import { queryKeys } from '@/app/utils/constants';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useFilterSortPaginate } from '../useFilterSortPaginate';
import { useTranslations } from 'use-intl';
import { toastManager } from '@/app/ui/components/toast';

export const useGetProjectsList = (
  organizationId: string,
  searchText: string = '',
  pageSize?: number,
  storageKey?: string,
) =>
  useFilterSortPaginate<APIProject>({
    queryKey: [queryKeys.PROJECTS, organizationId, searchText],
    fetchData: async (params) => {
      const { data } = await getProjectsList({
        organizationId,
        searchText,
        ...params,
      });
      return data;
    },
    initialSort: { key: 'latestActivity', direction: 'desc' },
    initialPageSize: pageSize,
    storageKey,
    enabled: !!organizationId,
  });

export const useGetProjectsInsights = (projectId: string, storageKey?: string) =>
  useFilterSortPaginate<Insight>({
    queryKey: [queryKeys.PROJECT_INSIGHTS, projectId],
    fetchData: async (params) => {
      const { data } = await getProjectInsights({
        projectId,
        ...params,
      });
      return data;
    },
    storageKey,
  });

export const useGetProjectDocument = (
  organizationId: string,
  projectId: string,
  documentId?: string,
) => {
  return useQuery({
    queryKey: [queryKeys.PROJECT_DOCUMENT, organizationId, projectId, documentId],
    queryFn: async () => {
      const { data } = await getProjectDocumentById({
        organizationId,
        projectId,
        documentId: documentId!,
      });
      return data;
    },
    enabled: !!organizationId && !!projectId && !!documentId,
  });
};

export const getProjectDetailsQuery = (projectId: string) =>
  queryOptions({
    staleTime: 900 * 1000,
    queryKey: [queryKeys.PROJECT_DETAILS, projectId],
    queryFn: async () => {
      const { data } = await getProjectDetails(projectId);
      return data;
    },
  });

export const useDeleteProject = (projectId: string) => {
  const t = useTranslations();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return await deleteProject(projectId);
    },
    onSuccess: async () => {
      toastManager.add({
        type: 'success',
        title: t('general.notifications.deletedSuccessfully'),
      });
      await queryClient.invalidateQueries({ queryKey: [queryKeys.PROJECTS] });
      await queryClient.invalidateQueries({ queryKey: [queryKeys.IS_USER_ON_BOARD] });
    },
  });
};
